import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Switch,
  Select,
  DatePicker,
  Flex,
  Breadcrumb,
} from "antd";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import {
  createOrderRequest,
  getOrderByIdRequest,
} from "../../../api/requests/order";
import { useContacts } from "../../../api/hooks/customer";
import { useProducts } from "../../../api/hooks/product";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import { useEffect } from "react";
import { createShipmentRequest } from "../../../api/requests/shipment";

const initialItemDetail = {
  product: undefined,
  quantity: 0,
};

const createOrderSchemaResolver = yupResolver(
  yup.object().shape({
    carrier: yup.string(),
    bols: yup
      .array()
      .of(
        yup.object().shape({
          order: yup.string(),
          items: yup
            .array()
            .of(
              yup.object().shape({
                product: yup.string(),
                quantity: yup
                  .number()
                  // .positive("Quantity must be a positive number")
                  .integer("Quantity must be an integer"),
              }),
            )
            .min(1, "Items must contain at least one item"),
        }),
      )
      .min(1, "BOLs must contain at least one entry"),
    shipmentDate: yup.date().typeError("Shipment Date must be a valid date"),
    driver: yup.object().shape({
      name: yup.string(),
      dl: yup.string(),
    }),
  }),
);

function AddShipmentInbound() {
  const params = useParams();
  const { orderId } = params;

  const { data: orderDetails } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => {
      const res = await getOrderByIdRequest({
        id: orderId,
        params: {},
      });
      return res.data?.data;
    },
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutateAsync: createShipment } = useMutation({
    mutationFn: async (data) => {
      const res = await createShipmentRequest(data);
      return res.data;
    },
    mutationKey: ["shipment/create"],
    onSuccess: (res) => {
      // queryClient.invalidateQueries(["order"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(`/processes/order/update/${orderId}`);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const { data: carrierList, isLoading: isLoadingCarrierList } = useContacts({
    params: {
      type: "CARRIER",
    },
  });

  const { data: productList, isLoading: isLoadingProductList } = useProducts({
    params: {},
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm({
    resolver: createOrderSchemaResolver,
    defaultValues: {
      // active: true,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bols[0].items",
  });

  console.log("errors----->", errors);
  console.log("getValues----->", getValues());
  const { bols = [] } = getValues();

  async function onSubmit(data) {
    // console.log(data);
    const res = await createShipment(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (orderDetails) {
      const { carrier } = orderDetails;
      reset({
        carrier: carrier?._id,
        bols: [
          {
            order: orderId,
            items: [initialItemDetail],
          },
        ],
      });
    }
  }, [orderDetails, reset]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={
                  [
                    // {
                    //   title: <Link to={"/processes/order"}>Orders</Link>,
                    // },
                    // {
                    //   title: <div className="text-lg">Create</div>,
                    // },
                  ]
                }
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
              >
                Save
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item label="Order ID" name="order.referenceId">
                    <Controller
                      control={control}
                      name="order.referenceId"
                      render={({ field }) => (
                        <Input
                          {...field}
                          value={orderDetails?.referenceId || ""}
                          disabled={true}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}></Col>
                <Col span={12}>
                  <Form.Item
                    label="Carrier"
                    name="carrier"
                    validateStatus={errors.carrier ? "error" : ""}
                    help={errors?.carrier?.message}
                    required={true}
                    wrapperCol={{ sm: 24 }}
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <Controller
                      control={control}
                      name="carrier"
                      render={({ field }) => (
                        <Select
                          {...field}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Carrier"
                          allowClear
                          loading={isLoadingCarrierList}
                          options={carrierList?.map(({ name, _id }) => ({
                            label: name,
                            value: _id,
                          }))}
                          style={{
                            textTransform: "capitalize",
                          }}
                          dropdownStyle={{
                            textTransform: "capitalize",
                          }}
                          disabled={true}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Shipment Date"
                    name="shipmentDate"
                    validateStatus={errors.shipmentDate ? "error" : ""}
                    help={errors.shipmentDate && errors.shipmentDate.message}
                    required={true}
                    wrapperCol={{ sm: 24 }}
                  >
                    <Controller
                      control={control}
                      name="shipmentDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          style={{
                            width: "100%",
                          }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Driver Name"
                    name="driver.name"
                    validateStatus={errors?.driver?.name ? "error" : ""}
                    help={errors?.driver?.name?.message}
                  >
                    <Controller
                      control={control}
                      name="driver.name"
                      render={({ field }) => (
                        <Input {...field} placeholder="Driver name" />
                      )}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Trailer ID"
                    name="driver.dl"
                    validateStatus={errors?.driver?.dl ? "error" : ""}
                    help={errors?.driver?.dl?.message}
                  >
                    <Controller
                      control={control}
                      name="driver.dl"
                      render={({ field }) => (
                        <Input {...field} placeholder="" />
                      )}
                    />
                  </Form.Item>
                </Col>

                {orderDetails?.type === "INBOUND" && (
                  <>
                    <Col span={24}>
                      <p className="text-lg">Items</p>
                    </Col>
                    {fields.map((field, index) => {
                      return (
                        <Col
                          span={24}
                          className="flex flex-grow items-end gap-5"
                          key={field.id}
                        >
                          <Form.Item
                            label="Product"
                            name={`bols.0.items.${index}.product`}
                            validateStatus={
                              errors?.bols?.[0]?.items?.[index]?.product
                                ? "error"
                                : ""
                            }
                            help={
                              errors?.bols?.[0]?.items?.[index]?.product
                                ?.message
                            }
                            wrapperCol={{ sm: 24 }}
                            className="flex-grow"
                          >
                            <Controller
                              control={control}
                              name={`bols.0.items.${index}.product`}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  showSearch
                                  optionFilterProp="label"
                                  placeholder="Product"
                                  allowClear
                                  loading={isLoadingProductList}
                                  options={productList?.map(({ name, _id }) => {
                                    return {
                                      label: name,
                                      value: _id,
                                      disabled: bols?.[0]?.items?.find(
                                        (item) => item?.product === _id,
                                      ),
                                    };
                                  })}
                                  style={{
                                    textTransform: "capitalize",
                                  }}
                                  dropdownStyle={{
                                    textTransform: "capitalize",
                                  }}
                                />
                              )}
                            />
                          </Form.Item>
                          <Form.Item
                            // className="w-full"
                            label="Quantity"
                            name={`bols.0.items.${index}.quantity`}
                            validateStatus={
                              errors?.bols?.[0]?.items?.[index]?.quantity
                                ? "error"
                                : ""
                            }
                            help={
                              errors?.bols?.[0]?.items?.[index]?.quantity
                                ?.message
                            }
                            className="flex-grow"
                          >
                            <Controller
                              control={control}
                              name={`bols.0.items.${index}.quantity`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  min={0}
                                  // step={0.01}
                                />
                              )}
                            />
                          </Form.Item>
                          {/* <Button
                            key={field?.id}
                            onClick={() => {
                              remove(index);
                            }}
                            className="mb-6"
                          >
                            <DeleteOutlined />
                          </Button> */}
                        </Col>
                      );
                    })}
                    {/* <Col span={24}>
                  <Flex justify="flex-end" gap={10}>
                    <Button
                      htmlType="button"
                      onClick={() => {
                        append(initialItemDetail);
                      }}
                    >
                      Add
                    </Button>
                  </Flex>
                </Col> */}
                  </>
                )}
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default AddShipmentInbound;
